import React, { useMemo, useState } from "react";
import {
  format,
  addDays,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
} from "date-fns";
import { formatDate } from "@/lib/dates";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as UiCalendar } from "@/components/ui/calendar";
import {
  Calendar,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  LayoutGrid,
} from "lucide-react";

export type RangeMode = "day" | "week" | "month";

const NZ_WEEK_STARTS_ON = 1 as const;

export const computeRangeForMode = (
  mode: RangeMode,
  anchor: Date
): { start: Date; end: Date; label: string } => {
  if (mode === "day") {
    return {
      start: anchor,
      end: anchor,
      label: `${format(anchor, "EEE")} ${formatDate(anchor)}`,
    };
  }
  if (mode === "week") {
    const ws = startOfWeek(anchor, { weekStartsOn: NZ_WEEK_STARTS_ON });
    const we = endOfWeek(anchor, { weekStartsOn: NZ_WEEK_STARTS_ON });
    return {
      start: ws,
      end: we,
      label: `${formatDate(ws)} – ${formatDate(we)}`,
    };
  }
  const ms = startOfMonth(anchor);
  const me = endOfMonth(anchor);
  return {
    start: ms,
    end: me,
    // Spelled-out month/year, not formatDate -- see ActualPerformanceTab.tsx.
    label: format(anchor, "MMMM yyyy"),
  };
};

type Props = {
  mode: RangeMode;
  onModeChange: (m: RangeMode) => void;
  anchor: Date;
  onAnchorChange: (d: Date) => void;
  allowedModes?: RangeMode[];
  showModeToggle?: boolean;
};

const RangeDatePicker: React.FC<Props> = ({
  mode,
  onModeChange,
  anchor,
  onAnchorChange,
  allowedModes = ["day", "week", "month"],
  showModeToggle = true,
}) => {
  const [pickerOpen, setPickerOpen] = useState(false);

  const { label } = useMemo(
    () => computeRangeForMode(mode, anchor),
    [mode, anchor]
  );

  const goBack = () => {
    if (mode === "day") onAnchorChange(addDays(anchor, -1));
    else if (mode === "week") onAnchorChange(addDays(anchor, -7));
    else onAnchorChange(subMonths(anchor, 1));
  };

  const goForward = () => {
    if (mode === "day") onAnchorChange(addDays(anchor, 1));
    else if (mode === "week") onAnchorChange(addDays(anchor, 7));
    else onAnchorChange(addMonths(anchor, 1));
  };

  const goToday = () => onAnchorChange(new Date());

  const handleDatePicked = (d: Date | undefined) => {
    if (!d) return;
    onAnchorChange(d);
    setPickerOpen(false);
  };

  const modeButton = (m: RangeMode, Icon: React.ComponentType<any>, lbl: string) => (
    <button
      key={m}
      onClick={() => onModeChange(m)}
      className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 transition-colors ${
        mode === m
          ? "bg-blue-600 text-white"
          : "text-gray-400 hover:text-white"
      }`}
    >
      <Icon className="h-3.5 w-3.5" /> {lbl}
    </button>
  );

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {showModeToggle && (
        <div className="flex items-center bg-gray-800 border border-gray-700 rounded-md overflow-hidden">
          {allowedModes.includes("day") && modeButton("day", Calendar, "Day")}
          {allowedModes.includes("week") &&
            modeButton("week", CalendarRange, "Week")}
          {allowedModes.includes("month") &&
            modeButton("month", LayoutGrid, "Month")}
        </div>
      )}

      <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            className="min-w-[210px] justify-between border-amber-500/60 text-amber-200 hover:text-amber-100"
          >
            <span>{label}</span>
            <ChevronsUpDown className="h-4 w-4 opacity-60" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-3 bg-gray-900 border-gray-700"
          align="end"
        >
          <div className="mb-2 text-sm font-medium text-white">
            {mode === "day"
              ? "Choose a day"
              : mode === "week"
              ? "Choose any day in the week"
              : "Choose a date in the month"}
          </div>
          <UiCalendar
            mode="single"
            selected={anchor}
            onSelect={handleDatePicked}
            initialFocus
            weekStartsOn={NZ_WEEK_STARTS_ON}
          />
        </PopoverContent>
      </Popover>

      <Button
        variant="outline"
        size="sm"
        onClick={goBack}
        className="border-amber-500/60 text-amber-200 hover:text-amber-100"
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={goToday}
        className="border-amber-500/60 text-amber-200 hover:text-amber-100"
      >
        Today
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={goForward}
        className="border-amber-500/60 text-amber-200 hover:text-amber-100"
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};

export default RangeDatePicker;
